import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import PageHeader from "../pages/PageHeader";
import axios from "axios";
import { FaLinkedin } from "react-icons/fa";
import { Link } from "react-router-dom";
import service_bg from "../assets/service-slider-bg.jpg";

const OurTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0); // Scroll to top on mount

    const fetchTeam = async () => {
      try {
        const response = await axios.get("https://apis.appnexts.com/api/team");
        if (Array.isArray(response.data)) {
          setTeamMembers(response.data);
        } else {
          setTeamMembers([]);
        }
      } catch (err) {
        console.error("Team fetch error:", err);
        setError("Failed to load team members. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();
  }, []);

  return (
    <div>
      {/* SEO Helmet */}
      <Helmet>
        <title>Our Team Members | AppNexts - Meet Our Digital Experts</title>
        <meta
          name="description"
          content="Meet the talented team behind AppNexts. Our experts specialize in web development, mobile apps, SEO, data scraping, and digital solutions tailored to your business needs."
        />
        <meta
          name="keywords"
          content="AppNexts team, our team, web development team, mobile app experts, SEO professionals, data scraping team, video editing experts, graphic designers"
        />
        <link rel="canonical" href="https://www.appnexts.com/our-team-members" />
        <meta property="og:title" content="Our Team | AppNexts - Meet Our Digital Experts" />
        <meta
          property="og:description"
          content="Get to know the AppNexts team – experienced professionals in development, design, marketing, and more, dedicated to growing your business digitally."
        />
        <meta property="og:image" content="https://www.appnexts.com/assets/images/team-banner.jpg" />
        <meta property="og:url" content="https://www.appnexts.com/our-team-members" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Team | AppNexts - Meet Our Digital Experts" />
        <meta
          name="twitter:description"
          content="Discover the creative minds powering AppNexts. Our team combines technical skills and strategic thinking to deliver innovative digital solutions."
        />
        <meta name="twitter:image" content="https://www.appnexts.com/assets/images/team-banner.jpg" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "AppNexts",
            "url": "https://www.appnexts.com",
            "logo": "https://www.appnexts.com/seo_image/appnexts.png",
            "employee": [
              {
                "@type": "Person",
                "name": "Muhammad Bilal Butt",
                "jobTitle": "Founder & CEO",
              },
              {
                "@type": "Person",
                "name": "Aqib Butt",
                "jobTitle": "Manager & Mern Stack Developer",
              },
              {
                "@type": "Person",
                "name": "Fardeen Butt",
                "jobTitle": "Flutter App Developer",
              },
            ],
          })}
        </script>
      </Helmet>

      {/* Page Header */}
      <PageHeader
        title="Our Team Members"
        breadcrumb={[
          { label: "Home", path: "/" },
          { label: "Our Team Members" },
        ]}
      />

      {/* Team Section */}
      <div
        className="bg-cover bg-center text-white py-12"
        style={{ backgroundImage: `url(${service_bg})` }}
      >
        <div className="mx-auto px-4 lg:px-8 font-manrope">
          <div className="mb-4">
            <h2 className="text-[#504e9a] font-semibold mb-2 text-xl sm:text-2xl">
              Team Members
            </h2>
          </div>
          <div className="mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Our Dedicated Team Members
            </h1>
          </div>

          {/* Loading */}
          {loading && (
            <div className="w-full py-10 flex justify-center items-center">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 border-4 border-t-transparent border-black rounded-full animate-spin"></div>
                <span className="text-black text-lg">Loading team members...</span>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="text-center text-red-200 mb-6">{error}</div>
          )}

          {/* Team Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {teamMembers.map((member) => (
                <a
                  key={member._id || member.id}
                  href={member.linkedin || "https://linkedin.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative flex flex-col group"
                >
                  <div className="relative overflow-hidden rounded-t-lg">
                    <img
                      src={member.image || "/default-avatar.jpg"}
                      alt={member.name}
                      className="object-cover object-top w-full h-full transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <FaLinkedin className="text-white text-4xl" />
                    </div>
                  </div>
                  <div className="bg-[#1e1b4b] text-white p-6 rounded-b-lg transition-colors duration-300 group-hover:bg-blue-600">
                    <h3 className="text-xl font-bold mb-1">{member.name}</h3>
                    <p className="text-gray-300">{member.designation}</p>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OurTeam;
